'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { lazy as lazyProductLightbox, Suspense as ProductLightboxBoundary } from 'react';

const LightboxComponent = lazyProductLightbox(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);

export enum ZSHOP_PICTURE {
  MAIN = '#ZSHOP_PICTURE_DETAIL_MAIN;',
  REZ = '#ZSHOP_PICTURE_REZ;',
}

export function ProductImages() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(s => s.product);
  //   const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const images = filerImages(
    templateSettings?.product_detail?.gallery_with_image_variants?.enabled ?? false,
    product as unknown as ProductDetailsWithSlug,
    // selectedVariant,
  );

  const thumbnailImages =
    images.map((image: any, i) => (
      <div
        key={image.id ?? image.url ?? i}
        className={cn(
          'relative flex h-full w-full items-center justify-center rounded-lg bg-card',
          'ea-product-detail-images__carousel-slide',
        )}
      >
        <Image
          alt={resolveImageAlt(image, product?.name)}
          src={image.url}
          fill
          sizes="(min-width: 1280px) 836px, (min-width: 768px) 67vw, calc(100vw - 32px)"
          priority={i === 0}
          fetchPriority={i === 0 ? 'high' : undefined}
          className={cn(
            'h-full w-full rounded-lg object-contain',
            'ea-product-detail-images__carousel-slide-image',
          )}
        />
      </div>
    )) ?? [];

  const { isOpen, setIsOpen } = useDisclosure();
  const thumbnails = images.slice(0, 7).map((image, i) => (
    <div key={image.id ?? image.url ?? i} className="relative aspect-video w-full">
      <Image
        alt={resolveImageAlt(image, product?.name)}
        src={image.url}
        fill
        sizes="(min-width: 1280px) 196px, 16vw"
        loading="lazy"
        className="rounded-lg object-contain"
      />
    </div>
  ));

  return (
    <div className={cn(' h-full max-h-[600px]', 'ea-product-detail-images')}>
      {isOpen && (
        <ProductLightboxBoundary fallback={null}>
          <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={images} />
        </ProductLightboxBoundary>
      )}
      <Carousel
        showThumbnails
        showDots
        options={{ loop: true }}
        slides={thumbnailImages.slice(0, 7)}
        thumbnailSlides={thumbnails}
        openLightbox={() => setIsOpen(true)}
        showMoreButton={thumbnailImages.length > 8}
        allImagesCount={thumbnailImages.length}
        className={{
          main: 'aspect-video w-full md:w-[75%] md:max-w-[67%]',
          wrapper: 'h-auto max-h-[572px] items-start',
          thumbnailsWrapper: 'max-w-[32%]',
          thumbnail: 'flex max-h-[125px] items-center justify-center',
        }}
      />
    </div>
  );
}

function filerImages(
  showOnlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const images =
    showOnlyVariantImages && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product?.image,
          ...productImages,
          ...variants.flatMap(variant => (Array.isArray(variant?.images) ? variant.images : [])),
        ];
  return images.filter((image): image is NonNullable<typeof image> => Boolean(imageSource(image)));
}
